import * as React from 'react';
import {Suspense, useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {ArrowLeft, Check, Clock} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {ClientReservationPageQuery} from './__generated__/ClientReservationPageQuery.graphql';
import {ClientReservationPageCancelMutation} from './__generated__/ClientReservationPageCancelMutation.graphql';

const QUERY = graphql`
    query ClientReservationPageQuery($id: ID!) {
        viewer { id }
        myReservation(id: $id) {
            id
            status
            startsAt
            endsAt
            days
            notes
            quotedTotal
            customerCancellable
            lines { id asset { assetTag name category kitMembers { id } dailyRate } }
        }
    }`;

const CANCEL = graphql`
    mutation ClientReservationPageCancelMutation($input: CancelMyReservationInput!) {
        cancelMyReservation(input: $input) {
            reservation { id status }
            userErrors { message }
        }
    }`;

const STEPS = ['REQUESTED', 'CONFIRMED', 'CHECKED_OUT', 'RETURNED'] as const;
const STEP_LABEL: Record<string, string> = {REQUESTED: 'Requested', CONFIRMED: 'Confirmed', CHECKED_OUT: 'Picked up', RETURNED: 'Returned'};
// Ops statuses the customer doesn't need to distinguish.
const stepIndex = (status: string) => {
    if (status === 'DRAFT') return 0;
    if (status === 'PICKED') return 1;
    return STEPS.indexOf(status as typeof STEPS[number]);
};

const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, {weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});

const Detail: React.FC<{ id: string; fetchKey: number; refresh: () => void }> = ({id, fetchKey, refresh}) => {
    // store-and-network, not network-only: the latter refetch-looped under the public Navbar.
    const data = useLazyLoadQuery<ClientReservationPageQuery>(QUERY, {id}, {fetchKey, fetchPolicy: 'store-and-network'});
    const [params] = useSearchParams();
    const [commit, inFlight] = useMutation<ClientReservationPageCancelMutation>(CANCEL);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const r = data.myReservation;

    if (!data.viewer) return <p className="text-gray-400">Please <Link to="/client" className="text-lumina-accent">sign in</Link> to see this reservation.</p>;
    if (!r) return <p className="text-gray-400">We couldn't find that reservation. <Link to="/client" className="text-lumina-accent">Back to your rentals</Link></p>;

    const cancel = () => commit({
        variables: {input: {reservationId: r.id}},
        onCompleted: (res) => {
            const payload = res.cancelMyReservation;
            if (payload?.reservation) { setConfirming(false); refresh(); } else setError(payload?.userErrors.map((u) => u.message).join('; ') ?? 'Something went wrong');
        },
        onError: (e) => setError(e.message),
    });

    const current = stepIndex(r.status);

    return (
        <div className="max-w-3xl">
            <Link to="/client" className="text-xs text-gray-500 hover:text-white flex items-center gap-1 mb-6"><ArrowLeft className="w-3 h-3"/> Your rentals</Link>

            {params.get('requested') && r.status === 'REQUESTED' && (
                <div className="mb-6 p-4 border border-lumina-accent/40 bg-lumina-accent/5 text-sm">
                    <p className="font-bold text-lumina-accent">Request received.</p>
                    <p className="text-gray-300">Your gear is on hold for these dates. We'll confirm shortly and follow up with pricing for anything marked "on request".</p>
                </div>
            )}

            <h1 className="text-3xl font-display font-bold mb-1">{fmt(r.startsAt)} → {fmt(r.endsAt)}</h1>
            <p className="text-gray-500 text-sm mb-6">{r.days} day{r.days === 1 ? '' : 's'}{r.quotedTotal && <> · estimate <span className="text-lumina-accent">${r.quotedTotal}</span></>}</p>

            {r.status === 'CANCELLED' ? (
                <p className="mb-8 text-red-400 font-bold tracking-widest text-sm uppercase">Cancelled</p>
            ) : (
                <ol className="flex items-center gap-2 mb-8">
                    {STEPS.map((step, i) => (
                        <li key={step} className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                i < current ? 'bg-lumina-accent text-black' : i === current ? 'bg-lumina-purple text-white' : 'border border-gray-700 text-gray-600'}`}>
                                {i < current ? <Check className="w-3 h-3"/> : i === current ? <Clock className="w-3 h-3"/> : i + 1}
                            </span>
                            <span className={`text-xs tracking-wider uppercase ${i <= current ? 'text-white' : 'text-gray-600'}`}>{STEP_LABEL[step]}</span>
                            {i < STEPS.length - 1 && <span className="w-6 h-px bg-gray-800"/>}
                        </li>
                    ))}
                </ol>
            )}

            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Gear</h2>
                <ul className="border border-gray-800 divide-y divide-gray-900">
                    {r.lines.map((line) => (
                        <li key={line.id} className="px-4 py-3 flex items-center gap-4">
                            <span className="flex-1">
                                <span className="text-white">{line.asset.name}</span>
                                <span className="block text-xs text-gray-500">
                                    {line.asset.category.replace(/_/g, ' ')}
                                    {line.asset.kitMembers.length > 0 && ` · ${line.asset.kitMembers.length} pieces`}
                                </span>
                            </span>
                            <span className="text-sm text-gray-400">{line.asset.dailyRate ? `$${line.asset.dailyRate}/day` : 'on request'}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {r.notes && (
                <section className="mb-8">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Your notes</h2>
                    <p className="text-gray-300 text-sm whitespace-pre-line">{r.notes}</p>
                </section>
            )}

            {r.customerCancellable && (
                <div className="flex items-center gap-3">
                    {confirming ? (
                        <>
                            <button onClick={cancel} disabled={inFlight} className="px-4 py-2 text-xs font-bold tracking-widest uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10">Yes, cancel it</button>
                            <button onClick={() => setConfirming(false)} className="px-4 py-2 text-xs font-bold tracking-widest uppercase border border-gray-700 text-gray-300">Keep it</button>
                        </>
                    ) : (
                        <button onClick={() => setConfirming(true)} className="px-4 py-2 text-xs font-bold tracking-widest uppercase border border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-500/40">Cancel this booking</button>
                    )}
                    {error && <span className="text-red-400 text-xs">{error}</span>}
                </div>
            )}
        </div>
    );
};

const ClientReservationPage: React.FC = () => {
    const {id = ''} = useParams();
    const [fetchKey, setFetchKey] = useState(0);
    const [, startTransition] = useTransition();
    return (
        <div className="bg-lumina-dark min-h-screen text-white">
            <Navbar/>
            <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
                <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                    <Detail id={decodeURIComponent(id)} fetchKey={fetchKey} refresh={() => startTransition(() => setFetchKey((k) => k + 1))}/>
                </Suspense>
            </main>
            <Footer/>
        </div>
    );
};

export default ClientReservationPage;
